import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency } from '../../utils/formatters';
import { Loader2, X, Edit3, Trash2, PlusCircle } from 'lucide-react';

const TransactionDrawer = ({ transactionId, onClose, onUpdated }) => {

  const [loading, setLoading] = useState(false);

  const [transaction, setTransaction] = useState(null);

  const [audits, setAudits] = useState([]);

  const [form, setForm] = useState({
    description: '',
    amount: '',
    category: '',
    transactionDate: '',
    flowDirection: 'outflow',
  });

  const [splitDrafts, setSplitDrafts] = useState([]);

  const [error, setError] = useState(null);

  const [saving, setSaving] = useState(false);

  const [savingSplits, setSavingSplits] = useState(false);

  const isOpen = Boolean(transactionId);



  const initializeFromDetail = (detail) => {

    const txn = detail.transaction;

    setTransaction(txn);

    setAudits(detail.audits || []);

    setForm({

      description: txn.description || '',

      amount: Number(txn.amount) || '',

      category: txn.category || '',

      transactionDate: txn.transaction_date ? txn.transaction_date.substring(0, 10) : '',

      flowDirection: txn.flow_direction || 'outflow',

    });

    setSplitDrafts(

      (detail.splits || []).map((split) => ({

        id: split.id,

        description: split.description || txn.description || '',

        category: split.category || txn.category || '',

        amount: Number(split.amount) || '',

      }))

    );

  };



  const fetchDetail = useCallback(async () => {

    if (!transactionId) return;

    setLoading(true);

    setError(null);

    try {

      const res = await axios.get(`/api/transactions/${transactionId}`);

      initializeFromDetail(res.data);

    } catch (err) {

      console.error(err);

      setError('Failed to load transaction details.');

    } finally {

      setLoading(false);

    }

  }, [transactionId]);



  useEffect(() => {

    if (transactionId) {

      fetchDetail();

    } else {

      setTransaction(null);

      setAudits([]);

      setSplitDrafts([]);

      setForm({ description: '', amount: '', category: '', transactionDate: '', flowDirection: 'outflow' });

      setError(null);

    }

  }, [transactionId, fetchDetail]);



  const handleFormChange = (field, value) => {

    setForm((prev) => ({ ...prev, [field]: value }));

  };



  const handleSaveTransaction = async () => {

    if (!transactionId) return;

    setSaving(true);

    setError(null);

    try {

      await axios.put(`/api/transactions/${transactionId}`, {

        description: form.description,

        transactionDate: form.transactionDate,

        amount: form.amount,

        category: form.category,

        flowDirection: form.flowDirection,

      });

      await fetchDetail();

      onUpdated?.();

    } catch (err) {

      console.error(err);

      setError(err.response?.data?.error || 'Failed to save changes.');

    } finally {

      setSaving(false);

    }

  };



  const handleSplitChange = (index, field, value) => {

    setSplitDrafts((prev) =>

      prev.map((split, idx) => (idx === index ? { ...split, [field]: value } : split))

    );

  };



  const addSplitRow = () => {

    setSplitDrafts((prev) => [

      ...prev,

      { id: `draft-${Date.now()}`, description: form.description || '', category: form.category || '', amount: '' },

    ]);

  };



  const removeSplitRow = (index) => {

    setSplitDrafts((prev) => prev.filter((_, idx) => idx !== index));

  };



  const transactionAmount = Number(form.amount);

  const normalizedAmount = Number.isFinite(transactionAmount) ? transactionAmount : 0;

  const splitTotal = splitDrafts.reduce((sum, split) => sum + Number(split.amount || 0), 0);

  const remainingAmount = normalizedAmount - splitTotal;



  const saveSplits = async () => {

    if (!transactionId) return;

    setSavingSplits(true);

    setError(null);

    try {

      await axios.put(`/api/transactions/${transactionId}/splits`, {

        splits: splitDrafts,

      });

      await fetchDetail();

      onUpdated?.();

    } catch (err) {

      console.error(err);

      setError(err.response?.data?.error || 'Failed to save splits.');

    } finally {

      setSavingSplits(false);

    }

  };



  const closeDrawer = () => {

    if (saving || savingSplits) return;

    onClose?.();

  };



  return (

    <div
      className={`fixed inset-0 z-50 transition-all duration-300 ${
        isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={closeDrawer}
      />

      <div
        className={`absolute right-0 top-0 h-full w-full max-w-2xl transform bg-white/95 shadow-[0_25px_65px_rgba(2,6,23,0.4)] transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-6 py-5">

            <div>

              <h2 className="text-2xl font-semibold text-slate-900">Edit Transaction</h2>

              {transaction && (

                <p className="text-sm text-slate-500">

                  {transaction.description} - {formatCurrency(transaction.amount, 2)}

                </p>

              )}

            </div>

            <button onClick={closeDrawer} className="rounded-full bg-white/70 p-2 text-slate-500 hover:text-slate-700">

              <X size={24} />

            </button>

          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">

            {loading ? (

            <div className="flex justify-center py-10">

              <Loader2 className="animate-spin text-indigo-600" size={32} />

            </div>

          ) : transaction ? (

            <>

              <div className="space-y-4 mb-6">

                <div>

                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">

                    Description

                  </label>

                  <input

                    type="text"

                    value={form.description}

                    onChange={(e) => handleFormChange('description', e.target.value)}

                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                  />

                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <div>

                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">

                      Date

                    </label>

                    <input

                      type="date"

                      value={form.transactionDate}

                      onChange={(e) => handleFormChange('transactionDate', e.target.value)}

                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                    />

                  </div>

                  <div>

                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">

                      Amount

                    </label>

                    <input

                      type="number"

                      min="0"

                      step="0.01"

                      value={form.amount}

                      onChange={(e) => handleFormChange('amount', e.target.value)}

                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                    />

                  </div>

                </div>

                <div>

                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">

                    Category

                  </label>

                  <input

                    type="text"

                    value={form.category}

                    onChange={(e) => handleFormChange('category', e.target.value)}

                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                  />

                </div>

                <div>

                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">

                    Flow Direction

                  </label>

                  <select

                    value={form.flowDirection}

                    onChange={(e) => handleFormChange('flowDirection', e.target.value)}

                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                  >

                    <option value="outflow">Outflow (money spent)</option>

                    <option value="inflow">Inflow (money received)</option>

                    <option value="transfer">Transfer</option>

                  </select>

                </div>

                <button

                  onClick={handleSaveTransaction}

                  disabled={saving}

                  className="w-full inline-flex justify-center px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-semibold hover:bg-indigo-700 disabled:bg-gray-400"

                >

                  {saving ? 'Saving...' : 'Save Changes'}

                </button>

              </div>



              <div className="border-t border-gray-200 pt-6 mb-6">

                <div className="flex items-center justify-between mb-3">

                  <div>

                    <h3 className="text-lg font-semibold text-gray-800">Splits</h3>

                    <p className="text-sm text-gray-500">

                      Allocate this transaction across budgets. Total must equal the transaction amount.

                    </p>

                  </div>

                  <button

                    onClick={addSplitRow}

                    className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"

                  >

                    Add Split

                  </button>

                </div>



                {splitDrafts.length === 0 ? (

                  <p className="text-sm text-gray-500">No splits yet.</p>

                ) : (

                  <div className="space-y-3">

                    {splitDrafts.map((split, index) => (

                      <div key={split.id} className="border border-gray-200 rounded-md p-3 space-y-2">

                        <div className="flex items-center justify-between">

                          <p className="text-xs text-gray-500">Split #{index + 1}</p>

                          <button

                            onClick={() => removeSplitRow(index)}

                            className="text-red-500 text-xs hover:text-red-700"

                          >

                            Remove

                          </button>

                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

                          <input

                            type="text"

                            placeholder="Description"

                            value={split.description}

                            onChange={(e) => handleSplitChange(index, 'description', e.target.value)}

                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                          />

                          <input

                            type="text"

                            placeholder="Category"

                            value={split.category}

                            onChange={(e) => handleSplitChange(index, 'category', e.target.value)}

                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                          />

                          <input

                            type="number"

                            min="0"

                            step="0.01"

                            placeholder="Amount"

                            value={split.amount}

                            onChange={(e) => handleSplitChange(index, 'amount', e.target.value)}

                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                          />

                        </div>

                      </div>

                    ))}

                  </div>

                )}



                <div className="flex items-center justify-between mt-3 text-sm">

                  <span className="text-gray-600">

                    Remaining: {formatCurrency(remainingAmount, 2)}

                  </span>

                  <button

                    onClick={saveSplits}

                    disabled={savingSplits || (splitDrafts.length > 0 && Math.abs(remainingAmount) > 0.01)}

                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 disabled:bg-gray-400"

                  >

                    {savingSplits ? 'Saving...' : 'Save Splits'}

                  </button>

                </div>

              </div>



              <div className="border-t border-gray-200 pt-6">

                <h3 className="text-lg font-semibold text-gray-800 mb-3">Audit History</h3>

                {audits.length === 0 ? (

                  <p className="text-sm text-gray-500">No changes recorded yet.</p>

                ) : (

                  <div className="space-y-3 max-h-64 overflow-y-auto pr-2">

                    {audits.map((audit) => (

                      <div key={audit.id} className="border border-gray-200 rounded-md p-3 text-sm text-gray-700">

                        <p className="text-xs text-gray-500 mb-1">

                          {new Date(audit.performed_at).toLocaleString()} - {audit.action}

                        </p>

                        <pre className="bg-gray-50 p-2 rounded text-xs overflow-x-auto">

                          {JSON.stringify({ previous: audit.previous_data, next: audit.new_data }, null, 2)}

                        </pre>

                      </div>

                    ))}

                  </div>

                )}

              </div>

            </>

          ) : (

            <p className="text-sm text-gray-500">Select a transaction to begin editing.</p>

          )}



          {error && (

            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>

          )}

        </div>

      </div>

    </div>

  </div>

  );

};
export default TransactionDrawer;
